import { Fragment, useCallback, useEffect, useState } from 'react'
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import { usePWAInstall } from './usePWAInstall'
import { Connexion, Inscription, Oubli, Reinitialisation } from './ecrans/Authentification'
import { Profil } from './ecrans/Profil'
import { Confidentialite } from './ecrans/Confidentialite'
import { InformationsLegales } from './ecrans/Legal'
import { FournisseurDeLangue, useTextes } from './LangueContexte'
import { FournisseurDeVille, useVille } from './VilleContexte'
import { FeuilleVille } from './ecrans/FeuilleVille'
import { AssistantPublication } from './ecrans/Publication'
import { FicheDetaillee, MesAnnonces } from './ecrans/Annonces'
import { Recherche } from './ecrans/Recherche'
import { Favoris, MesRecherches } from './ecrans/Suivi'
import { FilRoute, Messagerie } from './ecrans/Messagerie'
import { Dossier } from './ecrans/Dossier'
import { Lifestyle } from './ecrans/Lifestyle'
import { Recommandations } from './ecrans/Recommandations'
import { MesCandidatures } from './ecrans/Candidatures'
import { MesVisites } from './ecrans/Visites'
import { DetailBailEcran, MesBaux } from './ecrans/Bail'
import { MesEcheances, Quittances } from './ecrans/Loyer'
import { FicheVoteEcran, MesVotes } from './ecrans/VoteFoyer'
import { Foyer } from './ecrans/Foyer'
import { ClocheNotifications, Notifications, useNonLues } from './ecrans/Notifications'
import { Avis } from './ecrans/Avis'
import { AutresAnnonces, Introuvable, LimiteErreur, ServiceIndisponible } from './ecrans/Erreurs'
import { ajouterFavori } from './apiRecherche'
import { deconnecter, profil as chargerProfil, rafraichir, type Profil as ProfilDto } from './api'
import { Icone, Menu, SpriteIcones, type NomIcone } from './ui'
import { montant } from './formats'
import { CLE_DERNIERE_RECHERCHE, lireCriteres, type Criteres } from './criteres'
import {
  LANGUES,
  NOMS_DE_LANGUE,
  TEXTES,
  appliquerLangue,
  enregistrerLangue,
  urlDansLaLangue,
  type Langue,
} from './i18n'
import {
  THEMES,
  appliquerTheme,
  ecouterLeSysteme,
  enregistrerTheme,
  themeEffectif,
  themeInitial,
  type Theme,
} from './theme'

/**
 * Coquille de l'application publique.
 *
 * **Routeur depuis L3.** Les trois lots précédents s'en passaient — quatre écrans et trois états
 * suffisaient, et une dépendance de plus se serait payée en audit de licence (§15.1). L3 change la
 * donne : les filtres de recherche vivent dans la query string, une fiche doit être partageable, et
 * `docs/TODO.md` l'exige explicitement. Écrire cela à la main, ce n'est plus quarante lignes, c'est
 * un routeur — en moins bon, et à maintenir. `react-router-dom` est sous licence MIT ; la
 * contrainte du §15.1 porte sur les dépendances **payantes**.
 *
 * **Navigation refaite en L10a.** Elle comptait dix-sept liens à plat, un de plus à chaque lot :
 * personne ne conçoit une navigation comme cela, elle s'accumule. L'en-tête porte désormais la
 * recherche et les actions ; le reste vit dans un menu groupé en six sections, et les cinq
 * destinations les plus fréquentes sont dans une barre basse sur mobile. C'est la seule décision
 * du lot qui change la structure et non l'apparence.
 */
export function App({ langueInitiale: langueDeDepart }: { langueInitiale: Langue }) {
  // La langue ne change plus par un setState : elle est portee par l'URL, donc par le basename du
  // routeur. La bascule est une navigation, ce qui rend l'URL affichee partageable telle quelle.
  const langue = langueDeDepart
  const [profilCourant, setProfilCourant] = useState<ProfilDto | null>(null)
  const [pret, setPret] = useState(false)
  /** L'API n'a pas répondu au démarrage : rien ne s'affiche tant qu'elle ne répond pas. */
  const [panne, setPanne] = useState(false)

  // Le thème, lui, EST un état : il ne change pas l'URL, et le basculer ne doit pas recharger la
  // page. C'est toute la différence avec la langue, qui est portée par le `basename` du routeur.
  const [theme, setTheme] = useState<Theme>(themeInitial)

  const { installable, installer } = usePWAInstall()
  const emplacement = useLocation()

  useEffect(() => {
    appliquerLangue(langue)
    enregistrerLangue(langue)
  }, [langue])

  useEffect(() => {
    appliquerTheme(theme)
    enregistrerTheme(theme)

    // En mode « comme le système », la préférence peut changer sous nos pieds — au coucher du
    // soleil sur un téléphone en bascule automatique. Réappliquer suffit : `appliquerTheme`
    // relit le système à chaque appel.
    return ecouterLeSysteme(() => appliquerTheme(theme))
  }, [theme])

  const charger = useCallback(async () => {
    setPanne(false)

    // Le jeton d'accès vit en mémoire : un rechargement de page le perd. C'est le cookie de
    // rafraîchissement, HttpOnly, qui reconstitue la session — et son échec est le cas nominal
    // d'un visiteur non connecté, pas une panne.
    //
    // La panne, c'est l'autre échec : le réseau qui ne répond pas, ou le frontal sans API
    // derrière. Avant le 16/09/2026 il laissait « Chargement… » à l'écran pour toujours — ni
    // application, ni explication. Il a maintenant son état, avec « Réessayer ».
    try {
      if (await rafraichir()) {
        try {
          setProfilCourant(await chargerProfil())
        } catch {
          setProfilCourant(null)
        }
      }
    } catch {
      setPanne(true)
      return
    }

    setPret(true)
  }, [])

  useEffect(() => {
    void charger()
  }, [charger])

  const t = TEXTES[langue]
  const connecte = profilCourant !== null
  const estProprietaire = profilCourant?.activeRoles.includes('Owner') === true

  // Relu au montage, jamais poussé (D29). Un compteur absent est une gêne ; une connexion
  // permanente ouverte pour l'entretenir serait une dépense que l'usage ne justifie pas encore.
  const nonLues = useNonLues(connecte)

  return (
    <FournisseurDeLangue langue={langue}>
      {/*
        La ville (L13, D45) vit sous la langue : ses libellés en dépendent. La chaîne de résolution
        attend `pret` — la session reconstituée — parce que la ville du compte en est un maillon.
      */}
      <FournisseurDeVille pret={pret} connecte={connecte} preferenceCompte={profilCourant?.preferredCityCode}>
      <SpriteIcones />

      <Entete
        langue={langue}
        connecte={connecte}
        estProprietaire={estProprietaire}
        nonLues={nonLues}
        nomAffiche={profilCourant?.displayName ?? ''}
        theme={theme}
        onTheme={setTheme}
        onDeconnecte={() => setProfilCourant(null)}
      />

      {/*
        La fente de sous-en-tête — une barre pleine largeur, sous l'en-tête et hors de la
        coquille, dans laquelle un écran peut projeter sa propre barre d'outils.

        La recherche s'en sert pour ses filtres rapides (ticket `fo-recherche-01`) : la maquette
        les montre sur un fond blanc qui va d'un bord à l'autre, ce que la coquille — bornée et
        centrée — ne peut pas donner. Un portail plutôt qu'une prop, parce que l'état des filtres
        appartient à l'écran de recherche et n'a rien à faire dans la coquille.
      */}
      <div id="sous-entete" />

      <main className="coquille">
        {panne && <ServiceIndisponible onReessayer={() => void charger()} sortie={false} />}
        {!pret && !panne && <p className="socle">{t.chargement}</p>}

        {/*
          La limite d'erreur entoure les ÉCRANS, pas l'en-tête : une page cassée doit laisser le
          menu, pour qu'on puisse en partir. Le chemin en clé réarme la limite à chaque navigation.
        */}
        {pret && (
          <LimiteErreur cle={emplacement.pathname}>
          <Routes>
            <Route path="/" element={<Navigate to="/recherche" replace />} />

            {/*
              Le catalogue est consultable SANS compte, et c'est délibéré : « ne pas ouvrir
              publiquement avant 50 annonces » suppose que les visiteurs voient d'abord l'offre.
              Exiger une inscription pour regarder ferait fuir exactement les gens qu'on cherche.
            */}
            <Route path="/recherche" element={<Recherche connecte={connecte} />} />
            {/* La feuille de choix de la ville — sans compte, comme la recherche qu'elle prépare. */}
            <Route path="/ville" element={<FeuilleVille connecte={connecte} />} />
            <Route path="/annonces/:id" element={<FicheRoutee langue={langue} connecte={connecte} />} />

            <Route
              path="/connexion"
              element={
                <Anonyme connecte={connecte}>
                  <EcranConnexion langue={langue} onConnecte={charger} />
                </Anonyme>
              }
            />
            <Route
              path="/inscription"
              element={
                <Anonyme connecte={connecte}>
                  <EcranInscription langue={langue} onConnecte={charger} />
                </Anonyme>
              }
            />
            <Route
              path="/mot-de-passe-oublie"
              element={
                <Anonyme connecte={connecte}>
                  <EcranOubli langue={langue} />
                </Anonyme>
              }
            />
            <Route path="/reinitialiser" element={<EcranReinitialisation langue={langue} />} />

            <Route
              path="/favoris"
              element={
                <Protege connecte={connecte} emplacement={emplacement.pathname}>
                  <Favoris />
                </Protege>
              }
            />
            <Route
              path="/mes-recherches"
              element={
                <Protege connecte={connecte} emplacement={emplacement.pathname}>
                  <MesRecherches />
                </Protege>
              }
            />
            <Route
              path="/messages"
              element={
                <Protege connecte={connecte} emplacement={emplacement.pathname}>
                  <Messagerie langue={langue} moi={profilCourant?.id ?? null} />
                </Protege>
              }
            />
            <Route
              path="/messages/:id"
              element={
                <Protege connecte={connecte} emplacement={emplacement.pathname}>
                  <FilRoute langue={langue} moi={profilCourant?.id ?? null} />
                </Protege>
              }
            />
            <Route
              path="/recommandations"
              element={
                <Protege connecte={connecte} emplacement={emplacement.pathname}>
                  <Recommandations langue={langue} />
                </Protege>
              }
            />
            <Route
              path="/mode-de-vie"
              element={
                <Protege connecte={connecte} emplacement={emplacement.pathname}>
                  <Lifestyle langue={langue} />
                </Protege>
              }
            />
            <Route
              path="/mon-dossier"
              element={
                <Protege connecte={connecte} emplacement={emplacement.pathname}>
                  <Dossier langue={langue} nomAffiche={profilCourant?.displayName ?? ''} />
                </Protege>
              }
            />
            <Route
              path="/mes-candidatures"
              element={
                <Protege connecte={connecte} emplacement={emplacement.pathname}>
                  <MesCandidatures langue={langue} />
                </Protege>
              }
            />
            <Route
              path="/visites"
              element={
                <Protege connecte={connecte} emplacement={emplacement.pathname}>
                  <MesVisites langue={langue} moi={profilCourant?.id ?? null} />
                </Protege>
              }
            />
            <Route
              path="/avis"
              element={
                <Protege connecte={connecte} emplacement={emplacement.pathname}>
                  <Avis langue={langue} moi={profilCourant?.id ?? null} />
                </Protege>
              }
            />
            <Route
              path="/notifications"
              element={
                <Protege connecte={connecte} emplacement={emplacement.pathname}>
                  <Notifications langue={langue} />
                </Protege>
              }
            />
            <Route
              path="/mes-baux"
              element={
                <Protege connecte={connecte} emplacement={emplacement.pathname}>
                  <MesBaux langue={langue} moi={profilCourant?.id ?? null} />
                </Protege>
              }
            />
            <Route
              path="/baux/:id"
              element={
                <Protege connecte={connecte} emplacement={emplacement.pathname}>
                  <DetailBailEcran langue={langue} moi={profilCourant?.id ?? null} />
                </Protege>
              }
            />
            <Route
              path="/echeances"
              element={
                <Protege connecte={connecte} emplacement={emplacement.pathname}>
                  <MesEcheances langue={langue} moi={profilCourant?.id ?? null} />
                </Protege>
              }
            />
            <Route
              path="/quittances"
              element={
                <Protege connecte={connecte} emplacement={emplacement.pathname}>
                  <Quittances langue={langue} />
                </Protege>
              }
            />
            <Route
              path="/foyer"
              element={
                <Protege connecte={connecte} emplacement={emplacement.pathname}>
                  <Foyer langue={langue} moi={profilCourant?.id ?? null} />
                </Protege>
              }
            />
            <Route
              path="/avis-du-foyer"
              element={
                <Protege connecte={connecte} emplacement={emplacement.pathname}>
                  <MesVotes langue={langue} />
                </Protege>
              }
            />
            <Route
              path="/avis-du-foyer/:id"
              element={
                <Protege connecte={connecte} emplacement={emplacement.pathname}>
                  <FicheVoteEcran langue={langue} />
                </Protege>
              }
            />
            <Route
              path="/mes-annonces"
              element={
                <Protege connecte={connecte} emplacement={emplacement.pathname}>
                  <MesAnnonces langue={langue} moi={profilCourant?.id ?? null} />
                </Protege>
              }
            />
            <Route
              path="/publier"
              element={
                <Protege connecte={connecte} emplacement={emplacement.pathname}>
                  <PublicationRoutee />
                </Protege>
              }
            />
            <Route
              path="/profil"
              element={
                <Protege connecte={connecte} emplacement={emplacement.pathname}>
                  {profilCourant !== null && (
                    <Profil
                      langue={langue}
                      profil={profilCourant}
                      onRafraichir={setProfilCourant}
                      onDeconnecte={() => setProfilCourant(null)}
                    />
                  )}
                </Protege>
              }
            />
            <Route
              path="/confidentialite"
              element={
                <Protege connecte={connecte} emplacement={emplacement.pathname}>
                  <Confidentialite />
                </Protege>
              }
            />

            {/* Accessible SANS compte : un pied de page se lit avant de s'inscrire, et c'est
                souvent pour decider si l'on s'inscrit. */}
            <Route path="/informations-legales" element={<InformationsLegales />} />

            <Route path="*" element={<PageIntrouvable />} />
          </Routes>
          </LimiteErreur>
        )}
      </main>

      <PiedDePage langue={langue} connecte={connecte} />

      <BarreBasse langue={langue} connecte={connecte} />

      {installable && (
        <button type="button" className="bouton-installer" onClick={installer}>
          {t.installer}
        </button>
      )}
      </FournisseurDeVille>
    </FournisseurDeLangue>
  )
}

/**
 * L'en-tête.
 *
 * Il porte quatre choses et pas une de plus : l'identité, la recherche, ce qui appelle une
 * réaction (notifications), et l'accès au reste. Tout le reste est dans le menu — c'est la
 * différence entre une navigation et une liste de liens.
 */
function Entete({
  langue,
  connecte,
  estProprietaire,
  nonLues,
  nomAffiche,
  theme,
  onTheme,
  onDeconnecte,
}: {
  langue: Langue
  connecte: boolean
  estProprietaire: boolean
  nonLues: number
  nomAffiche: string
  theme: Theme
  onTheme: (theme: Theme) => void
  onDeconnecte: () => void
}) {
  const t = TEXTES[langue]

  return (
    <header className="entete">
      <div className="entete__rangee">
        <Link className="logo" to="/recherche">
          <span className="logo__marque">
            <Icone nom="maison" taille="sm" />
          </span>
          <span>{t.titre}</span>
        </Link>

        <PastilleDeRecherche langue={langue} />

        <div className="entete__actions">
          <MenuDeTheme langue={langue} theme={theme} onTheme={onTheme} />
          <MenuDeLangue langue={langue} />

          {connecte ? (
            <>
              <Link className="bouton bouton--discret entete__publier" to="/publier">
                {t.navPublier}
              </Link>

              <ClocheNotifications langue={langue} nonLues={nonLues} />

              <MenuCompte
                langue={langue}
                estProprietaire={estProprietaire}
                nomAffiche={nomAffiche}
                onDeconnecte={onDeconnecte}
              />
            </>
          ) : (
            <>
              {/*
                Un seul des deux tient en compact — la rangée débordait de l'écran sous 400 px — et
                c'est « Se connecter » qui reste (14/09/2026). Celui qui revient cherche sa session,
                pas un formulaire d'inscription ; et l'écran de connexion porte déjà « Pas de
                compte ? S'inscrire », donc rien ne se ferme. L'inverse fermait bien quelque chose :
                il fallait deviner que « S'inscrire » menait aussi au retour.
              */}
              <Link className="bouton bouton--discret entete__connexion" to="/connexion">
                {t.seConnecter}
              </Link>
              <Link className="bouton entete__inscription" to="/inscription">
                {t.sInscrire}
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  )
}

/**
 * La pastille de recherche de l'en-tête — elle porte les filtres posés, pas un libellé fixe.
 *
 * **Le défaut corrigé** (ticket `fo-transverse-01`) : elle affichait « Rechercher un logement »
 * quoi qu'on ait cherché. Une pastille qui ne dit pas ce qu'elle contient n'est plus un rappel de
 * la recherche en cours, c'est un bouton — et il faut retourner sur l'écran pour savoir ce qu'on
 * avait demandé.
 *
 * **Pourquoi `sessionStorage` et non l'URL seule.** Les critères vivent dans la query string de
 * `/recherche`, et l'en-tête est visible partout. Sur une fiche d'annonce, l'URL ne porte plus
 * rien : sans mémoire, la pastille redeviendrait muette exactement au moment où elle sert le plus,
 * c'est-à-dire quand on est parti regarder un résultat. La mémoire est volatile parce que la
 * recherche l'est aussi — un onglet fermé n'a pas de recherche en cours.
 */
function PastilleDeRecherche({ langue }: { langue: Langue }) {
  const t = TEXTES[langue]
  const { pathname, search } = useLocation()
  const ville = useVille()
  const [derniere, setDerniere] = useState(() => {
    try {
      return globalThis.sessionStorage.getItem(CLE_DERNIERE_RECHERCHE) ?? ''
    } catch {
      return ''
    }
  })

  useEffect(() => {
    if (!pathname.startsWith('/recherche')) {
      return
    }

    setDerniere(search)

    try {
      globalThis.sessionStorage.setItem(CLE_DERNIERE_RECHERCHE, search)
    } catch {
      /* stockage refusé : la pastille suivra l'URL courante, sans mémoire au-delà */
    }
  }, [pathname, search])

  const criteres = lireCriteres(derniere)

  // Le premier segment est la VILLE (L13) : celle de la dernière recherche si elle en porte une,
  // sinon la ville courante. Il l'était déjà — « Salé Al Jadida » — mais figé dans une clé i18n.
  const segments = segmentsDeRecherche(
    criteres,
    ville.libelle(criteres.cityCode ?? ville.courante?.code) || null,
    t,
    langue,
  )

  return (
    <Link
      className="recherche-pastille"
      to={`/recherche${derniere}`}
      aria-label={t.navRechercherLogement}
    >
      {segments.map((segment, rang) => (
        <Fragment key={segment}>
          {rang > 0 && <span className="recherche-pastille__sep" />}
          <span
            className={
              rang === 0 ? 'recherche-pastille__segment' : 'recherche-pastille__segment est-sourd'
            }
          >
            {segment}
          </span>
        </Fragment>
      ))}
      <span className="recherche-pastille__loupe">
        <Icone nom="loupe" taille="sm" />
      </span>
    </Link>
  )
}

/**
 * Les trois segments de la pastille : où, quoi, combien.
 *
 * Trois au maximum, et dans cet ordre — c'est celui dans lequel on énonce une recherche de
 * logement à quelqu'un. Au-delà, la pastille déborde sur les actions de l'en-tête ; le compte
 * exact des filtres posés est de toute façon sur la puce « Filtres », qui le porte déjà.
 *
 * Sans aucun critère, on retombe sur le libellé d'invitation : « Rechercher un logement » est
 * juste quand il n'y a rien à rappeler — sauf que la ville, elle, se rappelle toujours (D45 : la
 * puce de ville est toujours visible). Sans critère, la pastille dit donc la ville et l'invitation.
 */
function segmentsDeRecherche(
  criteres: Criteres,
  ville: string | null,
  t: (typeof TEXTES)['fr'],
  langue: Langue,
): string[] {
  const segments: string[] = []

  segments.push(criteres.text ?? criteres.district ?? ville ?? t.navRechercherLogement)

  const types: Record<string, string> = {
    EntireProperty: t.typeLogementEntier,
    PrivateRoom: t.typeChambrePrivee,
    SharedRoom: t.typeChambrePartagee,
  }

  if (criteres.unitKind !== undefined) {
    segments.push(types[criteres.unitKind])
  }

  if (criteres.maxRent !== undefined) {
    segments.push(`${t.rechercheJusqua} ${montant(criteres.maxRent, 'MAD', langue)}`)
  }

  // Rien de posé : la pastille invite au lieu de rappeler — la ville en tête, quand on la connaît.
  if (segments.length === 1 && criteres.text === undefined && criteres.district === undefined) {
    return ville === null ? [t.navRechercherLogement] : [ville, t.navRechercherLogement]
  }

  return segments
}

/**
 * Le sélecteur de langue — un menu depuis qu'il y a trois langues.
 *
 * **Pourquoi ce n'est plus un bouton.** À deux langues, un bouton portant le nom de l'autre suffit
 * et se comprend d'un coup d'œil. À trois, le même bouton devrait dire « la suivante », ce qui
 * n'est plus une information : on ne sait pas où l'on va, et il faut cliquer deux fois pour
 * revenir. Le menu montre les trois d'un coup et rend le choix direct.
 *
 * **Chaque nom est écrit dans sa propre langue** et porte son `lang`, sans quoi le synthétiseur
 * vocal lit « العربية » avec les règles du français.
 */
function MenuDeLangue({ langue }: { langue: Langue }) {
  const t = TEXTES[langue]

  return (
    <Menu
      ariaLabel={t.langue}
      classeDeclencheur="entete__icone"
      aligne="debut"
      declencheur={() => <Icone nom="globe" />}
    >
      <p className="menu__titre">{t.langue}</p>
      {LANGUES.map((code) => (
        <button
          key={code}
          type="button"
          lang={code}
          aria-current={code === langue ? 'true' : undefined}
          /*
            Navigation COMPLÈTE et non un changement d'état : le préfixe de langue est le
            `basename` du routeur, et un routeur ne change pas de `basename` en cours de route.
            Recharger sur la nouvelle URL est aussi ce qui rend la bascule partageable — l'URL
            affichée après le clic est celle qu'on peut envoyer à quelqu'un.
          */
          onClick={() => {
            if (code === langue) {
              return
            }

            enregistrerLangue(code)
            window.location.assign(
              urlDansLaLangue(code, window.location.pathname) + window.location.search,
            )
          }}
        >
          <Icone nom={code === langue ? 'coche' : 'globe'} taille="sm" />
          <span>{NOMS_DE_LANGUE[code]}</span>
        </button>
      ))}
    </Menu>
  )
}

/**
 * Le sélecteur de thème — trois états, dont « comme le système » qui est le défaut.
 *
 * L'icône du déclencheur montre le thème EFFECTIF, pas le réglage : en mode système, ce qui
 * intéresse est ce qu'on voit, et un troisième pictogramme pour « automatique » n'apprendrait
 * rien qu'on ne lise déjà à l'écran.
 */
function MenuDeTheme({
  langue,
  theme,
  onTheme,
}: {
  langue: Langue
  theme: Theme
  onTheme: (theme: Theme) => void
}) {
  const t = TEXTES[langue]
  const libelles: Record<Theme, string> = {
    systeme: t.themeSysteme,
    clair: t.themeClair,
    sombre: t.themeSombre,
  }

  return (
    <Menu
      ariaLabel={t.theme}
      classeDeclencheur="entete__icone"
      /* Aligné sur le DÉBUT de la barre : ces deux menus s'ouvrent depuis son premier tiers, et
         un panneau aligné sur la fin débordait de 54 px à gauche en 420 px de large. */
      aligne="debut"
      declencheur={() => <Icone nom={themeEffectif(theme) === 'sombre' ? 'lune' : 'soleil'} />}
    >
      <p className="menu__titre">{t.theme}</p>
      {THEMES.map((candidat) => (
        <button
          key={candidat}
          type="button"
          aria-current={candidat === theme ? 'true' : undefined}
          onClick={() => onTheme(candidat)}
        >
          <Icone
            nom={candidat === theme ? 'coche' : candidat === 'sombre' ? 'lune' : 'soleil'}
            taille="sm"
          />
          <span>{libelles[candidat]}</span>
        </button>
      ))}
    </Menu>
  )
}

/**
 * Le menu du compte — six sections.
 *
 * Le groupement n'est pas cosmétique : dix-sept liens à plat ne se lisent pas, six titres de
 * trois entrées se parcourent. Les groupes suivent le PARCOURS — on cherche, on candidate, on
 * signe, on habite — et non l'ordre des lots qui les ont livrés.
 */
function MenuCompte({
  langue,
  estProprietaire,
  nomAffiche,
  onDeconnecte,
}: {
  langue: Langue
  estProprietaire: boolean
  nomAffiche: string
  onDeconnecte: () => void
}) {
  const t = TEXTES[langue]
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const entree = (chemin: string, icone: NomIcone, libelle: string) => (
    <Link key={chemin} to={chemin} aria-current={pathname === chemin ? 'page' : undefined}>
      <Icone nom={icone} taille="sm" />
      <span>{libelle}</span>
    </Link>
  )

  const groupes: { titre: string; entrees: React.ReactNode[] }[] = [
    {
      titre: t.navGroupeRecherche,
      entrees: [
        entree('/recherche', 'loupe', t.rechercheChercher),
        entree('/favoris', 'coeur', t.suiviFavoris),
        entree('/mes-recherches', 'signet', t.suiviRecherches),
        entree('/recommandations', 'etoile', t.recommandations),
      ],
    },
    {
      titre: t.navGroupeCandidature,
      entrees: [
        entree('/messages', 'message', t.messages),
        entree('/mes-candidatures', 'fichier', t.mesCandidatures),
        entree('/visites', 'calendrier', t.visites),
        entree('/mon-dossier', 'fichier', t.monDossier),
        entree('/mode-de-vie', 'reglages', t.lifestyleTitre),
      ],
    },
    {
      titre: t.navGroupeLocation,
      entrees: [
        entree('/mes-baux', 'cle', t.mesBaux),
        entree('/echeances', 'monnaie', t.echeancier),
        entree('/quittances', 'fichier', t.quittances),
      ],
    },
    {
      titre: t.navGroupeFoyer,
      entrees: [
        entree('/foyer', 'groupe', t.foyer),
        entree('/avis-du-foyer', 'balance', t.voteFoyer),
        entree('/avis', 'etoile', t.avisTitre),
      ],
    },
  ]

  if (estProprietaire) {
    groupes.push({
      titre: t.navGroupeProprietaire,
      entrees: [
        entree('/mes-annonces', 'maison', t.annoncesMes),
        entree('/publier', 'plus', t.navPublier),
      ],
    })
  }

  groupes.push({
    titre: t.navGroupeCompte,
    entrees: [
      entree('/profil', 'utilisateur', t.monProfil),
      entree('/notifications', 'cloche', t.notifications),
      entree('/confidentialite', 'bouclier', t.confidentialite),

      /*
        **Se déconnecter, ici** — ticket `fo-transverse-06`.

        Elle n'existait que dans l'écran « Mon profil », c'est-à-dire à trois clics de partout.
        Se déconnecter est une action qu'on veut faire depuis n'importe où et tout de suite —
        souvent parce qu'on n'est pas sur son propre appareil, ce qui est exactement le moment où
        il ne faut pas avoir à chercher.

        Le même appel que dans le profil, et non un second chemin : `deconnecter()` purge le cache
        du service worker (invariant 17), et une deuxième écriture aurait fini par l'oublier. La
        purge est dans la fonction précisément pour que ce genre de duplication reste sans
        conséquence.

        Un bouton et non un lien : rien n'est navigué, et un `<a>` casserait le clic du milieu en
        promettant une page qui n'existe pas.
      */
      <button
        key="deconnexion"
        type="button"
        onClick={async () => {
          await deconnecter()
          onDeconnecte()
          navigate('/recherche')
        }}
      >
        <Icone nom="sortie" taille="sm" />
        <span>{t.seDeconnecter}</span>
      </button>,
    ],
  })

  return (
    <Menu
      ariaLabel={t.navMenuCompte}
      declencheur={() => (
        <>
          <Icone nom="menu" taille="sm" />
          <span className="avatar avatar--sm" aria-hidden="true">
            {initiales(nomAffiche)}
          </span>
        </>
      )}
    >
      {groupes.map((groupe, rang) => (
        <div key={groupe.titre}>
          {rang > 0 && <hr className="menu__separateur" />}
          <p className="menu__titre">{groupe.titre}</p>
          {groupe.entrees}
        </div>
      ))}
    </Menu>
  )
}

/**
 * Le pied de page — ticket `fo-transverse-07`.
 *
 * Le testeur a demandé de proposer. Ce qui suit est une proposition, pas une transcription : la
 * maquette n'en porte pas, et l'application n'en avait aucun.
 *
 * **Trois colonnes, et chacune répond à une question différente.** « Où puis-je aller ? » pour le
 * produit, « à qui je parle si ça casse ? » pour l'aide, « à qui ai-je affaire ? » pour le légal.
 * Un pied de page qui empile vingt liens ne répond à aucune des trois — c'est le défaut par
 * défaut du genre, et c'est celui qu'on évite en groupant.
 *
 * **Ce qu'il ne duplique pas.** Ni la navigation du compte, ni la barre basse : un pied de page
 * n'est pas une seconde navigation, c'est ce qu'on va chercher quand on est arrivé au bout. Les
 * trois liens produit sont les trois destinations qui ont un sens sans compte, parce que c'est
 * sans compte qu'on lit un pied de page.
 *
 * **La marque vient d'`i18n` et de nulle part ailleurs** (invariant 15) : `Coloc` est un nom de
 * code technique, et l'écrire en dur ici en ferait une marque un jour affichée à un utilisateur.
 *
 * **L'adresse de support est un lien `mailto:` et non un formulaire.** Un formulaire de contact
 * suppose un traitement côté serveur qui n'existe pas ; un `mailto:` marche le jour où on le pose
 * et laisse à l'utilisateur une trace de ce qu'il a envoyé.
 */
function PiedDePage({ langue, connecte }: { langue: Langue; connecte: boolean }) {
  const t = TEXTES[langue]

  return (
    <footer className="pied">
      <div className="pied__rangee">
        <nav className="pied__colonne" aria-label={t.piedProduit}>
          <p className="pied__titre">{t.piedProduit}</p>
          <Link to="/recherche">{t.rechercheChercher}</Link>
          <Link to="/publier">{t.navPublier}</Link>
          {connecte && <Link to="/recommandations">{t.recommandations}</Link>}
        </nav>

        <nav className="pied__colonne" aria-label={t.piedAide}>
          <p className="pied__titre">{t.piedAide}</p>
          <a href={`mailto:${ADRESSE_SUPPORT}`}>{t.piedContact}</a>
          <a href={`mailto:${ADRESSE_SUPPORT}?subject=${encodeURIComponent(t.piedSignalerObjet)}`}>
            {t.piedSignaler}
          </a>
        </nav>

        <nav className="pied__colonne" aria-label={t.piedLegal}>
          <p className="pied__titre">{t.piedLegal}</p>
          <Link to="/informations-legales#conditions">{t.legalConditions}</Link>
          <Link to="/informations-legales#confidentialite">{t.legalConfidentialite}</Link>
          <Link to="/informations-legales#mentions">{t.legalMentions}</Link>
          {/* « Mes données » n'est pas un document mais un OUTIL — l'écran par lequel s'exercent
              les droits de la loi 09-08. Il est ici parce que c'est ici qu'on le cherche. */}
          {connecte && <Link to="/confidentialite">{t.confidentialite}</Link>}
        </nav>
      </div>

      <p className="pied__mention">
        © {new Date().getFullYear()} {t.titre} — {t.piedLieu}
      </p>
    </footer>
  )
}

/**
 * L'adresse de support, en un seul endroit.
 *
 * Doublon assumé de `BrandOptions.SupportEmail` côté serveur : le front n'a aucun point d'accès
 * qui la lui donne, et en ouvrir un pour une chaîne constante coûterait un aller-retour réseau au
 * chargement de chaque page. Le jour où la marque change (D17), ce sont deux lignes à changer, et
 * celle-ci est nommée pour être trouvée.
 */
const ADRESSE_SUPPORT = 'support@atneo-solutions.com'

/** Deux lettres, sans accent de casse : « Youssef A. » → « YA ». */
function initiales(nom: string): string {
  const morceaux = nom.trim().split(/\s+/).filter((morceau) => morceau !== '')

  if (morceaux.length === 0) {
    return '?'
  }

  return (morceaux[0][0] + (morceaux.length > 1 ? morceaux[morceaux.length - 1][0] : '')).toUpperCase()
}

/**
 * La barre d'onglets basse — mobile uniquement, cinq destinations.
 *
 * Cinq, parce qu'au-delà les libellés se tronquent et que la barre cesse d'être lisible d'un
 * coup d'œil. Ce sont les cinq écrans où l'on revient plusieurs fois par jour ; tout le reste
 * s'atteint par le menu, à deux gestes au lieu d'un.
 */
function BarreBasse({ langue, connecte }: { langue: Langue; connecte: boolean }) {
  const t = TEXTES[langue]
  const { pathname } = useLocation()

  const entrees: { chemin: string; icone: NomIcone; libelle: string }[] = connecte
    ? [
        { chemin: '/recherche', icone: 'loupe', libelle: t.rechercheChercher },
        { chemin: '/messages', icone: 'message', libelle: t.messages },
        { chemin: '/favoris', icone: 'coeur', libelle: t.suiviFavoris },
        { chemin: '/foyer', icone: 'groupe', libelle: t.foyer },
        { chemin: '/profil', icone: 'utilisateur', libelle: t.monProfil },
      ]
    : [
        { chemin: '/recherche', icone: 'loupe', libelle: t.rechercheChercher },
        { chemin: '/connexion', icone: 'utilisateur', libelle: t.seConnecter },
      ]

  return (
    <nav className="barre-basse" aria-label={t.navPrincipale}>
      {entrees.map((entree) => (
        <Link
          key={entree.chemin}
          to={entree.chemin}
          aria-current={pathname === entree.chemin ? 'page' : undefined}
        >
          <Icone nom={entree.icone} />
          <span>{entree.libelle}</span>
        </Link>
      ))}
    </nav>
  )
}

/**
 * Route réservée aux comptes.
 *
 * Le chemin demandé est passé en paramètre de retour : après connexion, on revient là où l'on
 * voulait aller. Renvoyer systématiquement à l'accueil ferait perdre l'intention — et c'est
 * exactement ce que le §1.10 cherche à éviter sur le geste du favori.
 */
function Protege({
  connecte,
  emplacement,
  children,
}: {
  connecte: boolean
  emplacement: string
  children: React.ReactNode
}) {
  if (!connecte) {
    return <Navigate to={`/connexion?retour=${encodeURIComponent(emplacement)}`} replace />
  }

  return <>{children}</>
}

/** Un visiteur déjà connecté n'a rien à faire sur l'écran de connexion. */
function Anonyme({ connecte, children }: { connecte: boolean; children: React.ReactNode }) {
  if (connecte) {
    return <Navigate to="/recherche" replace />
  }

  return <>{children}</>
}

function FicheRoutee({ langue, connecte }: { langue: Langue; connecte: boolean }) {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  if (id === undefined) {
    return <PageIntrouvable />
  }

  return (
    <FicheDetaillee id={id} onRetour={() => navigate(-1)} langue={langue} connecte={connecte} />
  )
}

function PublicationRoutee() {
  const navigate = useNavigate()

  return <AssistantPublication onTermine={() => navigate('/mes-annonces')} />
}

/**
 * Connexion, avec reprise de l'intention.
 *
 * Deux choses sont reprises : le chemin d'où l'on venait, et le favori qu'on avait voulu poser
 * avant d'être arrêté. Le second vit en `sessionStorage` — un stockage volontairement volatil, il
 * ne s'agit que d'un geste en suspens, pas d'un état à conserver.
 */
function EcranConnexion({ langue, onConnecte }: { langue: Langue; onConnecte: () => Promise<void> }) {
  const navigate = useNavigate()
  const [params] = useSearchParams()

  return (
    <Connexion
      langue={langue}
      onNaviguer={(vue) => navigate(vue === 'inscription' ? '/inscription' : '/mot-de-passe-oublie')}
      onConnecte={async () => {
        await onConnecte()

        const enAttente = globalThis.sessionStorage.getItem('coloc:favori-en-attente')

        if (enAttente !== null) {
          globalThis.sessionStorage.removeItem('coloc:favori-en-attente')

          // Le geste repris ne doit jamais faire échouer la connexion : si l'annonce a disparu
          // entre-temps, on n'en dit rien et on continue.
          try {
            await ajouterFavori(enAttente)
          } catch {
            /* l'annonce n'existe plus, ou n'est plus publiée */
          }
        }

        navigate(params.get('retour') ?? '/recherche', { replace: true })
      }}
    />
  )
}

function EcranInscription({ langue, onConnecte }: { langue: Langue; onConnecte: () => Promise<void> }) {
  const navigate = useNavigate()

  return (
    <Inscription
      langue={langue}
      onNaviguer={() => navigate('/connexion')}
      onConnecte={async () => {
        await onConnecte()
        navigate('/recherche', { replace: true })
      }}
    />
  )
}

function EcranOubli({ langue }: { langue: Langue }) {
  const navigate = useNavigate()

  return <Oubli langue={langue} onNaviguer={() => navigate('/connexion')} onConnecte={() => undefined} />
}

function EcranReinitialisation({ langue }: { langue: Langue }) {
  const [params] = useSearchParams()
  const token = params.get('token')

  // Un lien sans jeton n'est pas une page inconnue : c'est un lien tronqué, et la sortie utile
  // est d'en demander un autre.
  if (token === null || token.length === 0) {
    return <Introuvable objet="lien" />
  }

  return <Reinitialisation langue={langue} token={token} />
}

/**
 * Page introuvable — revue le 16/09/2026 sur `docs/ui/pwa/introuvable.html` : l'état vide qui
 * nomme ce qui manque, l'explique et donne deux sorties, puis les dernières annonces de la ville.
 * Jamais une phrase seule (règle 10 du §6), et jamais un mur : on est venu chercher un logement.
 */
function PageIntrouvable() {
  const t = useTextes()

  return <Introuvable objet="page" suite={<AutresAnnonces titre={t.erreurSuitePage} />} />
}
